import { SESSION_KEYS, session } from './storage';

/**
 * Whether the patient has asked to leave their signup flow.
 *
 * `Router` forces a signup-in-progress patient to `getFlowByUrl(flowUrl)` on every route they land
 * on. For a patient with no weight-management history that destination is `/migrate-to-wm`, so any
 * attempt to be anywhere else is turned straight back — and because the guard reads only the
 * CURRENT pathname, a full page load at `/` is turned back for exactly the same reason an in-app
 * click is. There is no arrangement of navigation that escapes it; the guard has to be told.
 *
 * This is that telling, and it is deliberately narrow: only an explicit action by the patient sets
 * it, so nothing about the funnel changes for a patient who has not asked to leave.
 *
 * SESSION-scoped on purpose. It has to outlive a navigation — otherwise reaching Home only to be
 * pulled back on the next click is a worse experience than not offering the exit — but it must not
 * outlive the session, or one patient's choice would follow the next one into the same tab.
 * `useLogout` already calls `session.clear()`, so sign-out drops it with everything else.
 */
export const hasExitedFlow = (): boolean => session.get(SESSION_KEYS.FLOW_EXITED) === 'true';

/** Records that the patient asked to leave the flow. Call before navigating away from it. */
export const markFlowExited = (): void => session.set(SESSION_KEYS.FLOW_EXITED, 'true');

/**
 * Forgets the exit, so the forced redirect applies again.
 *
 * Used when the patient re-enters the flow deliberately: having chosen to go back in, they should
 * be kept there on the next route the way any other unfinished signup is.
 */
export const clearFlowExit = (): void => session.remove(SESSION_KEYS.FLOW_EXITED);
